import React, { useState, useRef, useEffect } from 'react';
import { Send, User, Loader2, Copy, Terminal, HelpCircle, Image as ImageIcon, Mic, X, ChevronDown, Check, Plus } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { atomDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { ChatMessage, AppSettings, ModelType, TRANSLATIONS } from '../types';

// Nular SVG Logo for Empty State
const NularLogoSVG = () => (
  <svg viewBox="0 0 24 24" fill="none" className="w-16 h-16 text-nular-dim opacity-20" xmlns="http://www.w3.org/2000/svg">
    <path d="M7 3.33782C8.47087 2.48697 10.1786 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 10.1786 2.48697 8.47087 3.33782 7" stroke="currentColor" strokeWidth="1" strokeLinecap="round"></path>
    <path d="M10.8613 9.36335C11.3679 8.45445 11.6213 8 12 8C12.3787 8 12.6321 8.45445 13.1387 9.36335L13.2698 9.59849C13.4138 9.85677 13.4858 9.98591 13.598 10.0711C13.7103 10.1563 13.8501 10.188 14.1296 10.2512L14.3842 10.3088C15.3681 10.5314 15.86 10.6427 15.977 11.0191C16.0941 11.3955 15.7587 11.7876 15.088 12.572L14.9144 12.7749C14.7238 12.9978 14.6285 13.1092 14.5857 13.2471C14.5428 13.385 14.5572 13.5336 14.586 13.831L14.6122 14.1018C14.7136 15.1482 14.7644 15.6715 14.4579 15.9041C14.1515 16.1367 13.6909 15.9246 12.7697 15.5005L12.5314 15.3907C12.2696 15.2702 12.1387 15.2099 12 15.2099C11.8613 15.2099 11.7304 15.2702 11.4686 15.3907L11.2303 15.5005C10.3091 15.9246 9.84847 16.1367 9.54206 15.9041C9.23565 15.6715 9.28635 15.1482 9.38776 14.1018L9.41399 13.831C9.44281 13.5336 9.45722 13.385 9.41435 13.2471C9.37147 13.1092 9.27617 12.9978 9.08557 12.7749L8.91204 12.572C8.2413 11.7876 7.90593 11.3955 8.02297 11.0191C8.14001 10.6427 8.63194 10.5314 9.61581 10.3088L9.87035 10.2512C10.1499 10.188 10.2897 10.1563 10.402 10.0711C10.5142 9.98591 10.5862 9.85677 10.7302 9.59849L10.8613 9.36335Z" stroke="currentColor" strokeWidth="1"></path>
  </svg>
);

interface ChatInterfaceProps {
  settings: AppSettings;
  messages: ChatMessage[];
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  updateSettings: (newSettings: Partial<AppSettings>) => void;
  showToast: (title: string, desc?: string, type?: 'info'|'success'|'error') => void;
}

const CodeBlock: React.FC<{ children: React.ReactNode, className?: string, onExplain: (code: string) => void, showToast: any }> = ({ children, className, onExplain, showToast }) => {
  const codeContent = String(children).replace(/\n$/, '');
  const match = /language-(\w+)/.exec(className || '');
  const language = match ? match[1] : 'text';
  const [hovered, setHovered] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(codeContent);
    showToast("Success", "Code Copied successfully", 'success');
  };

  return (
    <div 
      className="relative group my-4 rounded-lg overflow-hidden border border-nular-border bg-[#050505]"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="flex items-center justify-between px-4 py-2 bg-[#0a0a0a] border-b border-nular-border">
        <div className="flex items-center gap-2">
           <div className="text-[10px] font-mono text-nular-dim uppercase">{language}</div>
        </div>
        <div className="flex items-center gap-2">
           {hovered && (
             <button 
               onClick={() => onExplain(codeContent)}
               className="text-[10px] bg-nular-border hover:bg-nular-dim text-nular-silver px-2 py-1 rounded flex items-center gap-1 transition-colors"
             >
               <HelpCircle size={10} /> Ask
             </button>
           )}
           <button 
             onClick={handleCopy}
             className="text-nular-dim hover:text-nular-silver transition-colors"
           >
             <Copy size={14} />
           </button>
        </div>
      </div>
      <div className="text-sm font-mono leading-relaxed">
        <SyntaxHighlighter 
            language={language} 
            style={atomDark} 
            customStyle={{ margin: 0, padding: '1rem', background: 'transparent' }}
            wrapLongLines={true}
        >
            {codeContent}
        </SyntaxHighlighter>
      </div>
    </div>
  );
};

// Hugging Face Endpoints
const MODELS = {
  [ModelType.NULAR_FLASH]: "mistralai/Mistral-7B-Instruct-v0.3",
  [ModelType.NULAR_PRO]: "meta-llama/Meta-Llama-3-8B-Instruct",
  [ModelType.NULAR_O1]: "meta-llama/Meta-Llama-3-8B-Instruct" 
};

const ChatInterface: React.FC<ChatInterfaceProps> = ({ settings, messages, setMessages, updateSettings, showToast }) => {
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [attachment, setAttachment] = useState<{type: 'image' | 'audio', data: string} | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isModelDropdownOpen, setIsModelDropdownOpen] = useState(false);
  
  // Abort Control
  const abortControllerRef = useRef<AbortController | null>(null);
  
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const t = TRANSLATIONS[settings.language];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsModelDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle Image Upload
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = (reader.result as string).split(',')[1];
        setAttachment({ type: 'image', data: base64String });
        showToast("Success", "Image attached successfully", 'success');
        setIsMenuOpen(false);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle Voice Recording
  const startRecording = async () => {
    setIsMenuOpen(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      const chunks: Blob[] = [];

      mediaRecorder.ondataavailable = (e) => chunks.push(e.data);
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        const reader = new FileReader();
        reader.onloadend = () => {
           const base64 = (reader.result as string).split(',')[1];
           setAttachment({ type: 'audio', data: base64 });
           showToast("Success", "Voice note recorded successfully", 'success');
        };
        reader.readAsDataURL(blob);
        stream.getTracks().forEach(t => t.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (e) {
      showToast("Error", "Microphone access denied", 'error');
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setIsRecording(false);
  };

  const stopGeneration = () => {
     if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
     }
     setIsLoading(false);
  };

  // Helper to format prompts for Llama 3
  const formatLlama3 = (msgs: ChatMessage[], system: string) => {
    let prompt = `<|begin_of_text|><|start_header_id|>system<|end_header_id|>\n\n${system}<|eot_id|>`;
    msgs.forEach(msg => {
      if (msg.role === 'user') {
         prompt += `<|start_header_id|>user<|end_header_id|>\n\n${msg.text}<|eot_id|>`;
      } else {
         prompt += `<|start_header_id|>assistant<|end_header_id|>\n\n${msg.text}<|eot_id|>`;
      }
    });
    prompt += `<|start_header_id|>assistant<|end_header_id|>\n\n`;
    return prompt;
  };

  // Helper to format prompts for Mistral
  const formatMistral = (msgs: ChatMessage[], system: string) => {
    let prompt = "<s>";
    // Mistral typically puts system instructions inside the first [INST] block
    msgs.forEach((msg, index) => {
       if (msg.role === 'user') {
          if (index === 0) {
              prompt += `[INST] ${system}\n\n${msg.text} [/INST]`;
          } else {
              prompt += `[INST] ${msg.text} [/INST]`;
          }
       } else {
          prompt += `${msg.text}</s>`;
       }
    });
    return prompt;
  };

  const generateResponse = async (userText: string, att?: {type: 'image' | 'audio', data: string}) => {
    // Check HF Token
    const hfToken = process.env.HUGGING_FACE_TOKEN;
    if (!hfToken || hfToken.includes('YourTokenHere')) {
      showToast("Config Error", "HUGGING_FACE_TOKEN missing in .env", 'error');
      setMessages(prev => [...prev, {
        id: Date.now().toString(), role: 'model', text: 'Error: Please add HUGGING_FACE_TOKEN to .env file.', isError: true, timestamp: Date.now()
      }]);
      return;
    }

    setIsLoading(true);
    abortControllerRef.current = new AbortController();

    try {
      // 1. Prepare Context
      const now = new Date();
      let systemInstruction = `You are Nular, an advanced AI. Current time: ${now.toLocaleString()}. `;
      if (settings.userMemory) systemInstruction += `\nMemory: ${settings.userMemory}`;

      if (settings.model === ModelType.NULAR_FLASH) {
        systemInstruction += " Be concise and fast.";
      } else if (settings.model === ModelType.NULAR_PRO) {
        systemInstruction += " You are an expert coding assistant. Provide detailed code.";
      } else if (settings.model === ModelType.NULAR_O1) {
        systemInstruction += " You are a reasoning engine. Think step-by-step before answering.";
      }

      // 2. Prepare History
      // Note: HF Free Inference API doesn't support images/audio via text-generation endpoints easily. 
      // We will only use text history.
      const textHistory = messages.filter(m => !m.isError && m.text.trim());
      // Add current user message to history array for formatting
      const currentMsg: ChatMessage = { id: 'temp', role: 'user', text: userText, timestamp: Date.now() };
      const fullConversation = [...textHistory, currentMsg];

      // 3. Format Prompt
      let formattedPrompt = "";
      if (settings.model === ModelType.NULAR_FLASH) {
         formattedPrompt = formatMistral(fullConversation, systemInstruction);
      } else {
         formattedPrompt = formatLlama3(fullConversation, systemInstruction);
      }

      // 4. Call Hugging Face API
      const modelId = MODELS[settings.model];
      const response = await fetch(`https://api-inference.huggingface.co/models/${modelId}`, {
         method: "POST",
         headers: {
            "Authorization": `Bearer ${hfToken}`,
            "Content-Type": "application/json"
         },
         body: JSON.stringify({
            inputs: formattedPrompt,
            parameters: {
               max_new_tokens: 1024,
               temperature: settings.model === ModelType.NULAR_O1 ? 0.2 : 0.7,
               return_full_text: false,
               wait_for_model: true
            },
            stream: true // Enable streaming
         }),
         signal: abortControllerRef.current.signal
      });

      if (!response.ok) {
         throw new Error(`HF Error: ${response.status} ${response.statusText}`);
      }

      // 5. Handle Streaming Response
      if (!response.body) throw new Error("No response body");
      
      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      
      const responseId = (Date.now() + 1).toString();
      setMessages(prev => [...prev, {
        id: responseId,
        role: 'model',
        text: '',
        timestamp: Date.now()
      }]);

      let fullText = '';
      
      while (true) {
         const { value, done } = await reader.read();
         if (done) break;
         const chunk = decoder.decode(value, { stream: true });
         
         // HF Stream format: data: {"token": {"text": "..."}}
         // Sometimes it sends multiple 'data:' lines in one chunk
         const lines = chunk.split('\n');
         for (const line of lines) {
            if (line.startsWith('data:')) {
               try {
                  const jsonStr = line.replace('data:', '').trim();
                  if (jsonStr === '[DONE]') continue;
                  
                  const data = JSON.parse(jsonStr);
                  // Llama 3 / Mistral usually return 'token.text' or 'generated_text'
                  const newText = data.token?.text || data.generated_text || '';
                  
                  // Filter out special tokens if they appear raw
                  if (newText !== '<|eot_id|>' && newText !== '</s>') {
                     fullText += newText;
                     setMessages(prev => prev.map(msg => msg.id === responseId ? { ...msg, text: fullText } : msg));
                  }
               } catch (e) {
                  // ignore parse errors for partial lines
               }
            }
         }
      }

    } catch (error: any) {
      if (error.name === 'AbortError') return;
      console.error(error);
      
      let errorMsg = "Generation failed.";
      if (error.message.includes("429")) errorMsg = "Rate limit exceeded (HF Free Tier).";
      if (error.message.includes("503")) errorMsg = "Model is loading (Cold Start). Try again in 30s.";
      
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        role: 'model',
        text: `Error: ${errorMsg}`,
        isError: true,
        timestamp: Date.now()
      }]);
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
    }
  };

  const handleSend = async () => {
    if (isLoading) {
       stopGeneration();
       if (!input.trim()) return;
       await new Promise(r => setTimeout(r, 100));
    }

    if (!input.trim() && !attachment && !isRecording) return;
    
    // Check if user is trying to send attachments to HF Text models (not supported in this simple implementation)
    if (attachment) {
       showToast("Warning", "Attachments not supported with HF text models yet.", 'info');
    }

    const userMsg: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      text: input,
      timestamp: Date.now(),
      image: attachment?.type === 'image' ? attachment.data : undefined,
      audio: attachment?.type === 'audio' ? attachment.data : undefined
    };

    setMessages(prev => [...prev, userMsg]);
    setInput('');
    const tempAtt = attachment ? {...attachment} : undefined;
    setAttachment(null);
    
    await generateResponse(userMsg.text, tempAtt as any);
  };

  const handleExplainCode = (codeSnippet: string) => {
    const prompt = `Explain this code snippet briefly:\n\n${codeSnippet}`;
    const userMsg: ChatMessage = { id: Date.now().toString(), role: 'user', text: prompt, timestamp: Date.now() };
    setMessages(prev => [...prev, userMsg]);
    generateResponse(prompt);
  };

  // Determine button state
  const isTyping = input.trim().length > 0 || attachment !== null;
  
  // Model Display Names
  const modelDisplay = {
    [ModelType.NULAR_FLASH]: "Nular Flash (Mistral)",
    [ModelType.NULAR_PRO]: "Nular Pro (Llama 3)",
    [ModelType.NULAR_O1]: "Nular O1 (Reasoning)"
  };

  return (
    <div className="flex flex-col h-full bg-[#0f0f0f] relative">
      
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 p-4 z-20 flex justify-start pl-16 md:pl-6 bg-gradient-to-b from-[#0f0f0f] to-transparent pointer-events-none">
         <div className="pointer-events-auto relative" ref={dropdownRef}>
            <button 
              onClick={() => setIsModelDropdownOpen(!isModelDropdownOpen)}
              className="flex items-center gap-2 text-nular-silver hover:bg-nular-panel bg-[#0a0a0a]/80 backdrop-blur border border-nular-border px-3 py-1.5 rounded-lg transition-all text-sm font-medium shadow-sm group"
            >
               <span>{messages.length > 0 ? (messages[0].text.slice(0, 20) + (messages[0].text.length > 20 ? '...' : '')) : 'New Conversation'}</span>
               <span className="text-nular-dim">/</span>
               <span className="text-nular-dim group-hover:text-nular-silver">{modelDisplay[settings.model]}</span>
               <ChevronDown size={14} className="text-nular-dim" />
            </button>

            {isModelDropdownOpen && (
              <div className="absolute top-full left-0 mt-2 w-64 bg-[#0a0a0a] border border-nular-border rounded-lg shadow-xl overflow-hidden animate-fade-in flex flex-col z-50">
                 <div className="p-2 border-b border-nular-border text-[10px] text-nular-dim font-mono uppercase tracking-wider">Select Model</div>
                 
                 {[ModelType.NULAR_FLASH, ModelType.NULAR_PRO, ModelType.NULAR_O1].map(m => (
                    <button 
                      key={m}
                      onClick={() => { updateSettings({ model: m }); setIsModelDropdownOpen(false); }}
                      className="flex items-center justify-between px-4 py-3 hover:bg-nular-border/50 text-left text-sm text-nular-silver transition-colors"
                    >
                       <span>{modelDisplay[m]}</span>
                       {settings.model === m && <Check size={14} className="text-nular-accent"/>}
                    </button>
                 ))}
              </div>
            )}
         </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-8 pt-20">
        <div className="max-w-3xl mx-auto space-y-8">
          {messages.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-nular-dim opacity-40 min-h-[50vh]">
              <div className="mb-6 animate-pulse">
                <NularLogoSVG />
              </div>
              <p className="text-xl font-bold tracking-tight mb-2 text-nular-dim">NULAR</p>
            </div>
          )}

          {messages.map((msg) => (
            <div key={msg.id} className={`flex w-full ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`flex max-w-[90%] md:max-w-[85%] ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'} items-start gap-4`}>
                <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 border ${
                  msg.role === 'user' ? 'bg-nular-panel border-nular-border' : 'bg-nular-silver border-white text-black'
                }`}>
                  {msg.role === 'user' ? <User size={16} /> : <Terminal size={16} />}
                </div>
                
                <div className={`min-w-0 ${msg.role === 'user' ? 'text-right' : 'text-left'}`}>
                   <div className={`text-base md:text-lg leading-relaxed ${msg.role === 'user' ? 'text-nular-dim' : 'text-nular-silver'}`}>
                     {msg.image && <img src={`data:image/png;base64,${msg.image}`} className="max-w-xs rounded-lg mb-2 border border-nular-border" alt="User upload" />}
                     {msg.audio && <div className="text-xs mb-2 italic flex items-center gap-1"><Mic size={12}/> Audio Message</div>}
                     <ReactMarkdown components={{
                       code(props) {
                         const {children, className, node, ...rest} = props
                         const match = /language-(\w+)/.exec(className || '')
                         return match ? (
                           <CodeBlock className={className} onExplain={handleExplainCode} showToast={showToast}>
                             {children}
                           </CodeBlock>
                         ) : (
                           <code className={`${className} bg-nular-panel px-1.5 py-0.5 rounded text-nular-silver font-mono text-sm border border-nular-border`} {...rest}>
                             {children}
                           </code>
                         )
                       }
                     }}>
                       {msg.text}
                     </ReactMarkdown>
                   </div>
                </div>
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="flex justify-start w-full animate-pulse">
               <div className="flex max-w-[75%] flex-row items-start gap-4">
                 <div className="w-9 h-9 rounded-full bg-nular-panel border border-nular-border flex items-center justify-center flex-shrink-0">
                   <Loader2 size={16} className="animate-spin text-nular-dim" />
                 </div>
                 <div className="mt-2 text-sm font-mono text-nular-dim">
                    Thinking...
                 </div>
               </div>
            </div>
          )}
        </div>
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="p-6 bg-[#0f0f0f]">
        <div className="max-w-3xl mx-auto">
          
          {/* Textbox Container */}
          <div className="relative group bg-[#0a0a0a] border border-nular-border rounded-xl p-2 shadow-2xl transition-all hover:border-nular-dim/30 focus-within:border-nular-dim/50">
             
             {/* Attachment Preview */}
             {attachment && (
                <div className="absolute -top-12 left-0 bg-nular-panel border border-nular-border rounded-lg p-2 flex items-center gap-2 animate-fade-in shadow-lg">
                   {attachment.type === 'image' ? <ImageIcon size={14} className="text-purple-400"/> : <Mic size={14} className="text-red-400"/>}
                   <span className="text-xs text-nular-silver">{attachment.type === 'image' ? 'Image Attached' : 'Voice Note'}</span>
                   <button onClick={() => setAttachment(null)} className="hover:text-red-400"><X size={12}/></button>
                </div>
             )}

             {isRecording && (
                <div className="absolute inset-0 bg-red-900/10 backdrop-blur-sm z-10 rounded-xl flex items-center justify-center gap-4">
                   <div className="w-3 h-3 bg-red-500 rounded-full animate-ping"></div>
                   <span className="text-red-400 font-mono text-sm">Recording...</span>
                   <button onClick={stopRecording} className="px-4 py-1 bg-red-500/20 text-red-400 rounded-full text-xs border border-red-500/50 hover:bg-red-500/30">Stop & Attach</button>
                </div>
             )}

             <div className="flex items-end gap-2">
                <div className="flex flex-col gap-2 pb-2 pl-2 relative" ref={menuRef}>
                   <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleImageUpload} />
                   
                   <button 
                     onClick={() => setIsMenuOpen(!isMenuOpen)} 
                     className="text-nular-dim hover:text-nular-silver transition-colors p-2 rounded-lg hover:bg-nular-border"
                   >
                      <Plus size={20} className={`transition-transform duration-200 ${isMenuOpen ? 'rotate-45' : ''}`}/>
                   </button>

                   {/* Popup Menu */}
                   {isMenuOpen && (
                      <div className="absolute bottom-12 left-0 bg-nular-panel border border-nular-border rounded-xl shadow-xl p-1 min-w-[140px] flex flex-col gap-1 z-20 animate-fade-in">
                         <button onClick={() => fileInputRef.current?.click()} className="flex items-center gap-2 px-3 py-2 text-sm text-nular-dim hover:text-nular-silver hover:bg-nular-border/50 rounded-lg text-left">
                            <ImageIcon size={16} className="text-purple-400"/> Attach Photo
                         </button>
                         <button onClick={startRecording} className="flex items-center gap-2 px-3 py-2 text-sm text-nular-dim hover:text-nular-silver hover:bg-nular-border/50 rounded-lg text-left">
                            <Mic size={16} className="text-red-400"/> Voice Message
                         </button>
                      </div>
                   )}
                </div>

                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                  placeholder={t.typePlaceholder}
                  className="w-full bg-transparent text-nular-silver placeholder-nular-dim/40 border-none focus:ring-0 resize-none min-h-[44px] max-h-[200px] py-2.5 px-2 text-base font-sans leading-relaxed"
                  rows={1}
                />
                
                <button
                  onClick={handleSend}
                  disabled={(!input.trim() && !attachment && !isLoading)}
                  className={`mb-1 p-2.5 rounded-lg transition-all ${
                     // If loading AND (Not typing), show pause style
                     // If loading AND typing, show send style (interrupt)
                     isLoading && !isTyping
                     ? 'bg-nular-border text-nular-silver hover:bg-nular-border/80' 
                     : 'bg-nular-silver text-black hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed'
                  }`}
                >
                  {isLoading && !isTyping ? (
                     <div className="w-[18px] h-[18px] flex items-center justify-center">
                       <div className="w-2 h-2 bg-nular-silver rounded-sm" />
                     </div>
                  ) : (
                     <Send size={18} />
                  )}
                </button>
             </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatInterface;